import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from 'axios';
import { config } from 'config/config';

export interface TenantAppearanceValues {
    design?: string;
    terms?: string;
    logo?: string;
    favicon?: string;
    env?: string;
    title?: string;
    theme?: Record<string, string>;
}

export interface TenantAppearance {
    replace?: string;
    values?: TenantAppearanceValues;
}

const cacheKey = (host: string) => `appearance:${host}`;

const readCached = (host: string): TenantAppearance | null => {
    try {
        const raw = sessionStorage.getItem(cacheKey(host));
        return raw ? (JSON.parse(raw) as TenantAppearance) : null;
    } catch {
        return null;
    }
};

const writeCached = (host: string, data: TenantAppearance): void => {
    try {
        sessionStorage.setItem(cacheKey(host), JSON.stringify(data));
    } catch {
        // storage full / disabled — ignore
    }
};

let inFlight: Promise<TenantAppearance> | null = null;

const fetchAppearance = (host: string): Promise<TenantAppearance> => {
    const cached = readCached(host);
    if (cached) {
        return Promise.resolve(cached);
    }
    if (inFlight) {
        return inFlight;
    }
    inFlight = axios
        .get<TenantAppearance>(config.api.routes.backend.publicAppearance, {
            baseURL: config.api.baseUrl,
            params: { host },
        })
        .then((response) => {
            const data = response.data ?? {};
            writeCached(host, data);
            return data;
        })
        .catch(() => ({}))
        .finally(() => {
            inFlight = null;
        });
    return inFlight;
};

interface GlobalDesignContextValue {
    logo: string;
    setLogo: (value: string) => void;
    favicon: string;
    setFavicon: (value: string) => void;
    appearance: TenantAppearance;
    appearanceLoaded: boolean;
}

const initialGlobalDesignContextValue: GlobalDesignContextValue = {
    logo: '',
    setLogo: () => {},
    favicon: '',
    setFavicon: () => {},
    appearance: {},
    appearanceLoaded: false,
};

const GlobalDesignContext = createContext<GlobalDesignContextValue>(initialGlobalDesignContextValue);
export const useGlobalDesignContext = () => useContext(GlobalDesignContext);

interface Props {
    children: ReactNode;
}

const GlobalDesignContextProvider = ({ children }: Props) => {
    const host = window.location.hostname;
    const cached = readCached(host);

    const [logo, setLogo] = useState('');
    const [favicon, setFavicon] = useState('');
    const [appearance, setAppearance] = useState<TenantAppearance>(cached ?? {});
    const [appearanceLoaded, setAppearanceLoaded] = useState(!!cached);

    useEffect(() => {
        if (cached) {
            return;
        }
        let cancelled = false;
        fetchAppearance(host).then((data) => {
            if (cancelled) return;
            setAppearance(data);
            setAppearanceLoaded(true);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <GlobalDesignContext.Provider
            value={{ logo, setLogo, favicon, setFavicon, appearance, appearanceLoaded }}
        >
            {children}
        </GlobalDesignContext.Provider>
    );
};

export default GlobalDesignContextProvider;
